import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Input, DatePicker, Button, Row, Col, Space, Spin, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import { useOperatorAuth } from '@contexts/operator/OperatorAuthContext';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useFeatureEnabled } from '@contexts/operator/PlanContext';
import { workOrderService } from '@services/operator/workOrderService';
import { inventoryAttributeDefinitionService } from '@services/operator/inventoryAttributeDefinitionService';

import WorkOrderItemList from './WorkOrderItemList';
import ApplyBomButton from './ApplyBomButton';

interface InventoryTypeOption {
  id: string;
  name: string;
  [key: string]: unknown;
}

// NOTE: shape lifted to match WorkOrderItemList's InventoryAttributeDefinition exactly
// (label & data_type are required there, since the list renders them unconditionally).
// Backing schema (inventoryAttributeDefinitionService) guarantees both are non-null.
interface InventoryAttributeDefinition {
  id: string;
  name: string;
  label: string;
  data_type: string;
  required?: boolean;
  options?: unknown;
  [key: string]: unknown;
}

interface WorkOrderItem {
  id?: string;
  product_id?: string | null;
  product_sku?: string | null;
  product_name?: string | null;
  inventory_type_id?: string | null;
  inventory_type_name?: string | null;
  role: 'input' | 'output';
  quantity: number;
  batch?: string | null;
  expiration_date?: string | Dayjs | null;
  manufacturing_date?: string | Dayjs | null;
  note?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  [key: string]: unknown;
}

interface InitialWorkOrder {
  id: string;
  name: string;
  scheduled_at: string | null;
  notes: string | null;
  items: WorkOrderItem[];
  [key: string]: unknown;
}

interface WorkOrderFormProps {
  merchantId: string;
  merchant: { id?: string; name?: string };
  initialWorkOrder?: InitialWorkOrder;
  // merchant-scoped reference data — when present, supplied by an Edit wrapper
  // that uses `useRecordMerchantSync` to align OperatorDataContext.currentMerchant
  // to the loaded record's merchant. Keeps the form merchant-agnostic on the edit
  // path so race conditions between header `currentMerchant` and the record's
  // merchant cannot leak the wrong merchant's dropdowns into a Save.
  // When absent (e.g. New flow under <MerchantSelect>, unit tests), fall back to
  // reading OperatorDataContext directly — safe in those flows because the user
  // explicitly picks the merchant.
  inventoryTypes?: InventoryTypeOption[];
  inventoryAttributeDefinitions?: InventoryAttributeDefinition[];
  // True while the Edit wrapper is still switching context to the record's
  // merchant. Render-gated to avoid the form briefly showing the previous
  // merchant's options.
  merchantDataLoading?: boolean;
}

interface MerchantInventoryTypeEntry {
  inventory_type?: InventoryTypeOption | null;
  [key: string]: unknown;
}

const WorkOrderForm: React.FC<WorkOrderFormProps> = ({
  merchantId,
  merchant,
  initialWorkOrder,
  inventoryTypes: inventoryTypesProp,
  inventoryAttributeDefinitions: inventoryAttributeDefinitionsProp,
  merchantDataLoading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const [form] = Form.useForm();
  // Fall back to OperatorDataContext only when the wrapper didn't pre-filter
  // (New flow / tests). Edit wrapper passes a record-aligned list as prop.
  const operatorData = useOperatorData() as { merchantInventoryTypes?: MerchantInventoryTypeEntry[] };
  const inventoryTypesFallback: InventoryTypeOption[] = (operatorData?.merchantInventoryTypes || [])
    .map((mit) => mit.inventory_type)
    .filter((it): it is InventoryTypeOption => Boolean(it));
  const inventoryTypes: InventoryTypeOption[] = inventoryTypesProp ?? inventoryTypesFallback;
  const { operator } = useOperatorAuth() as { operator?: { warehouse_id?: string } };
  const bomFeatureEnabled = useFeatureEnabled('production_boms');

  const [submitting, setSubmitting] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [inventoryAttributeDefinitionsFallback, setInventoryAttributeDefinitionsFallback] = useState<InventoryAttributeDefinition[]>([]);
  const inventoryAttributeDefinitions: InventoryAttributeDefinition[] =
    inventoryAttributeDefinitionsProp ?? inventoryAttributeDefinitionsFallback;

  const isNew = !initialWorkOrder;

  // BOM-link live recalc + manual-edit break detection. ApplyBomButton stamps
  // bom_linked: true + bom_snapshot on each row it writes. This handler:
  //   1. Output qty change (on a linked output) → multiply per_set_quantity ×
  //      new output qty for every still-linked input.
  //   2. Linked input qty manually edited to a value that doesn't match
  //      per_set × output → break that row's link (bom_linked=false).
  //   3. Linked input product changed → break that row's link.
  //   4. Linked output product changed → break output's link AND all inputs'
  //      links (the recipe no longer matches, scaling is meaningless).
  // Re-entry guard: setFieldsValue inside this handler doesn't trigger
  // onValuesChange (antd contract), but the ref defends against future antd
  // behavior changes and against accidental recursion via product field edit.
  const isPatchingFromBomLogicRef = useRef(false);

  const extractProductId = (productIdField: unknown): string | null => {
    if (productIdField == null) return null;
    if (typeof productIdField === 'string') return productIdField;
    if (typeof productIdField === 'object' && 'value' in (productIdField as Record<string, unknown>)) {
      return ((productIdField as { value: string }).value) ?? null;
    }
    return null;
  };

  const handleValuesChange = useCallback((
    changed: Record<string, unknown>,
    allValues: Record<string, unknown>,
  ) => {
    if (isPatchingFromBomLogicRef.current) return;

    const outputItems = (allValues.output_items as Record<string, unknown>[] | undefined) || [];
    const inputItems = (allValues.input_items as Record<string, unknown>[] | undefined) || [];

    let patchedOutputs: Record<string, unknown>[] | null = null;
    let patchedInputs: Record<string, unknown>[] | null = null;
    const patchOutput = (idx: number, patch: Record<string, unknown>) => {
      patchedOutputs = patchedOutputs || [...outputItems];
      patchedOutputs[idx] = { ...(patchedOutputs[idx] || {}), ...patch };
    };
    const patchInput = (idx: number, patch: Record<string, unknown>) => {
      patchedInputs = patchedInputs || [...inputItems];
      patchedInputs[idx] = { ...(patchedInputs[idx] || {}), ...patch };
    };

    const outputChanges = changed.output_items as Array<Record<string, unknown> | undefined> | undefined;
    const inputChanges = changed.input_items as Array<Record<string, unknown> | undefined> | undefined;

    // (4) Output product change on a linked output → unlink output + every linked input.
    if (Array.isArray(outputChanges)) {
      outputChanges.forEach((rowChange, idx) => {
        if (!rowChange) return;
        const row = outputItems[idx];
        if (!row || !row.bom_linked) return;
        if ('product_id' in rowChange) {
          const newId = extractProductId(rowChange.product_id);
          const snapId = (row.bom_snapshot as { product_id?: string } | undefined)?.product_id;
          if (newId !== snapId) {
            patchOutput(idx, { bom_linked: false });
            inputItems.forEach((inp, i) => {
              if (inp?.bom_linked) patchInput(i, { bom_linked: false });
            });
          }
        }
      });
    }

    // (1) Output[0] qty change on a (still) linked output → recompute linked inputs.
    if (Array.isArray(outputChanges) && outputChanges[0]?.quantity !== undefined) {
      const newOutputQty = outputChanges[0].quantity as number | null | undefined;
      const output0 = (patchedOutputs?.[0] ?? outputItems[0]) as Record<string, unknown> | undefined;
      if (output0?.bom_linked && typeof newOutputQty === 'number' && newOutputQty > 0) {
        inputItems.forEach((inp, i) => {
          const current = (patchedInputs?.[i] ?? inp) as Record<string, unknown> | undefined;
          if (!current?.bom_linked) return;
          const perSet = (current.bom_snapshot as { per_set_quantity?: number } | undefined)?.per_set_quantity;
          if (typeof perSet === 'number') {
            patchInput(i, { quantity: perSet * newOutputQty });
          }
        });
      }
    }

    // (2)(3) Manual edit on a linked input → break link.
    if (Array.isArray(inputChanges)) {
      inputChanges.forEach((rowChange, idx) => {
        if (!rowChange) return;
        const row = inputItems[idx];
        if (!row?.bom_linked) return;

        if (rowChange.quantity !== undefined) {
          const output0Qty = ((patchedOutputs?.[0] ?? outputItems[0]) as { quantity?: number } | undefined)?.quantity;
          const perSet = (row.bom_snapshot as { per_set_quantity?: number } | undefined)?.per_set_quantity;
          if (typeof perSet === 'number' && typeof output0Qty === 'number') {
            const expected = perSet * output0Qty;
            if (rowChange.quantity !== expected) {
              patchInput(idx, { bom_linked: false });
            }
          }
        }

        if ('product_id' in rowChange) {
          const newId = extractProductId(rowChange.product_id);
          const snapId = (row.bom_snapshot as { product_id?: string } | undefined)?.product_id;
          if (newId !== snapId) {
            patchInput(idx, { bom_linked: false });
          }
        }
      });
    }

    if (patchedOutputs || patchedInputs) {
      const patch: Record<string, unknown> = {};
      if (patchedOutputs) patch.output_items = patchedOutputs;
      if (patchedInputs) patch.input_items = patchedInputs;
      isPatchingFromBomLogicRef.current = true;
      form.setFieldsValue(patch);
      // Defer the guard release until after antd's internal updates settle.
      queueMicrotask(() => {
        isPatchingFromBomLogicRef.current = false;
      });
    }
  }, [form]);

  const handleSuggestName = async () => {
    try {
      setSuggesting(true);
      const res = await workOrderService.suggestName(merchantId);
      if (res.success) {
        form.setFieldsValue({ name: res.data.name });
      } else {
        console.error('[WorkOrderForm] suggestName returned error envelope:', res);
        message.error(t('shared.work_orders.form.suggest_name_error'));
      }
    } catch (err) {
      console.error('[WorkOrderForm] suggestName failed:', err);
      message.error(t('shared.work_orders.form.suggest_name_error'));
    } finally {
      setSuggesting(false);
    }
  };

  // 獲取庫存自定義屬性定義 — 只有當 wrapper 沒有透過 prop 注入時才自行 fetch。
  // Edit wrapper 會用 record 對齊的 merchantId 自己 fetch 並注入，這裡跳過避免 double fetch
  // 與在 edit-mode race 期間打到舊 merchant 的 endpoint。
  useEffect(() => {
    if (inventoryAttributeDefinitionsProp !== undefined) return;
    const fetchDefinitions = async () => {
      if (!merchantId) return;
      try {
        const result = await inventoryAttributeDefinitionService.fetchList({ merchant_id: merchantId });
        if (result?.success || result?.data) {
          setInventoryAttributeDefinitionsFallback((result.data as InventoryAttributeDefinition[]) || []);
        }
      } catch (error) {
        console.error('Failed to fetch inventory attribute definitions', error);
      }
    };
    fetchDefinitions();
  }, [merchantId, inventoryAttributeDefinitionsProp]);

  useEffect(() => {
    if (initialWorkOrder) {
        const mapItemToForm = (item: WorkOrderItem) => ({
            ...item,
            // ProductItemCard 的 Select 使用 labelInValue 模式，需要 { value, label } 格式
            product_id: item.product_id ? {
                value: item.product_id,
                label: `${item.product_sku || ''} - ${item.product_name || ''}`,
            } : null,
            inventory_type_id: item.inventory_type_id ? {
                value: item.inventory_type_id,
                label: item.inventory_type_name || '',
            } : null,
            expiration_date: item.expiration_date ? dayjs(item.expiration_date as string) : null,
            manufacturing_date: item.manufacturing_date ? dayjs(item.manufacturing_date as string) : null,
            custom_attributes: item.custom_attributes || {},
        });

        form.setFieldsValue({
            name: initialWorkOrder.name,
            scheduled_at: initialWorkOrder.scheduled_at ? dayjs(initialWorkOrder.scheduled_at) : null,
            notes: initialWorkOrder.notes,
            input_items: initialWorkOrder.items.filter((i) => i.role === 'input').map(mapItemToForm),
            output_items: initialWorkOrder.items.filter((i) => i.role === 'output').map(mapItemToForm),
        });
    }
  }, [initialWorkOrder, form]);

  const onFinishFailed = ({ errorFields }: { errorFields: Array<{ name: (string | number)[] }> }) => {
    if (errorFields.length > 0) {
      message.error(t('common.validation.form_has_errors'));
      form.scrollToField(errorFields[0].name);
    }
  };

  // 將 form values 轉成 API payload
  const buildPayload = (values: Record<string, unknown>) => {
    const warehouseId = operator?.warehouse_id;
    const inputs = (values.input_items as WorkOrderItem[]) || [];
    const outputs = (values.output_items as WorkOrderItem[]) || [];

    // 轉換 item 的 product_id 和 inventory_type_id 從 { value, label } 格式提取實際 ID。
    // bom_linked / bom_snapshot 是 ApplyBomButton 寫進去、純前端用來驅動連動 UX 的
    // metadata，BE 不接受也不該被 silent-drop（之後 BE 加同名欄位會撞 schema） — 明確 strip。
    const mapItemToPayload = (item: WorkOrderItem, role: 'input' | 'output') => {
      const { bom_linked: _bl, bom_snapshot: _bs, ...rest } = item as WorkOrderItem & {
        bom_linked?: boolean;
        bom_snapshot?: unknown;
      };
      return {
        ...rest,
        role,
        product_id: (rest.product_id as { value: string } | string | null | undefined)?.['value' as keyof typeof rest.product_id]
          ?? rest.product_id,
        inventory_type_id: (rest.inventory_type_id as { value: string } | string | null | undefined)?.['value' as keyof typeof rest.inventory_type_id]
          ?? rest.inventory_type_id,
        expiration_date: (rest.expiration_date as Dayjs)?.format?.('YYYY-MM-DD')
          ?? rest.expiration_date,
        manufacturing_date: (rest.manufacturing_date as Dayjs)?.format?.('YYYY-MM-DD')
          ?? rest.manufacturing_date,
      };
    };

    return {
      merchant_id: merchantId,
      warehouse_id: warehouseId,
      name: values.name,
      scheduled_at: values.scheduled_at,
      notes: values.notes,
      items_attributes: [
        ...outputs.map((item) => mapItemToPayload(item, 'output')),
        ...inputs.map((item) => mapItemToPayload(item, 'input')),
      ],
    };
  };

  // 核心提交邏輯：組 payload 後呼叫 create/update
  const submitValues = async (values: Record<string, unknown>) => {
    const payload = buildPayload(values);
    return initialWorkOrder
      ? await workOrderService.update(initialWorkOrder.id, payload)
      : await workOrderService.create(payload);
  };

  // 主要提交：成功後導向該筆記錄的 show 頁
  const onFinish = async (values: Record<string, unknown>) => {
    // Validation: Ensure both Inputs and Outputs are present
    const inputs = (values.input_items as WorkOrderItem[]) || [];
    const outputs = (values.output_items as WorkOrderItem[]) || [];

    if (inputs.length === 0 || outputs.length === 0) {
        message.error(t('operator.work_orders.form.items_required'));
        return;
    }

    setSubmitting(true);
    try {
        const res = await submitValues(values);

        if (res.success) {
            message.success(initialWorkOrder ? t('common.update_success') : t('common.create_success'));
            // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
            const recordId = res.data.id || initialWorkOrder?.id;
            navigate(`/work_orders/${recordId}`, { replace: true });
        } else {
            message.error(res.message || (initialWorkOrder ? t('common.update_failed') : t('common.create_failed')));
        }
    } catch (e) {
        console.error(e);
        message.error(initialWorkOrder ? t('common.update_failed') : t('common.create_failed'));
    } finally {
        setSubmitting(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      const inputs = (values.input_items as WorkOrderItem[]) || [];
      const outputs = (values.output_items as WorkOrderItem[]) || [];

      if (inputs.length === 0 || outputs.length === 0) {
        message.error(t('operator.work_orders.form.items_required'));
        return;
      }

      setSavingAndAdding(true);
      try {
        const res = await submitValues(values);

        if (res.success) {
          message.success(t('common.create_success'));
          // 重置 antd Form 欄位（包含 Form.List input_items / output_items）
          form.resetFields();
        } else {
          message.error(res.message || t('common.create_failed'));
        }
      } catch (e) {
        console.error(e);
        message.error(t('common.create_failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  return (
    <>
    <Spin spinning={merchantDataLoading}>
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={handleValuesChange}
      scrollToFirstError
      initialValues={{
          scheduled_at: dayjs(),
          input_items: [],
          output_items: [],
      }}
    >
      <div>
        <Row gutter={16}>
            <Col span={12}>
                <Form.Item
                    label={t('models.work_order.name')}
                    required
                >
                    <Space.Compact style={{ width: '100%' }}>
                        <Form.Item
                            name="name"
                            noStyle
                            rules={[{ required: true, message: t('common.validation.required') }]}
                        >
                            <Input placeholder={t('common.placeholder.name')} />
                        </Form.Item>
                        {isNew && (
                            <Button
                                onClick={handleSuggestName}
                                loading={suggesting}
                                disabled={submitting || savingAndAdding}
                            >
                                {t('shared.work_orders.form.suggest_name')}
                            </Button>
                        )}
                    </Space.Compact>
                </Form.Item>
            </Col>
            <Col span={12}>
                <Form.Item
                    name="scheduled_at"
                    label={t('models.work_order.scheduled_at')}
                    rules={[{ required: true, message: t('common.validation.required') }]}
                >
                    <DatePicker style={{ width: '100%' }} showTime placeholder={t('common.placeholder.select_date')} />
                </Form.Item>
            </Col>
        </Row>
        <Form.Item name="notes" label={t('models.work_order.notes')}>
            <Input.TextArea rows={2} />
        </Form.Item>
      </div>

      <WorkOrderItemList
        form={form}
        name="output_items"
        label={t('operator.work_orders.detail.outputs_title')}
        description={t('operator.work_orders.detail.outputs_description')}
        merchantId={merchantId}
        inventoryTypes={inventoryTypes}
        inventoryAttributeDefinitions={inventoryAttributeDefinitions}
        role="output"
        headerExtra={isNew && bomFeatureEnabled ? (
          <ApplyBomButton form={form} merchantId={merchantId} />
        ) : null}
      />

      <WorkOrderItemList
        form={form}
        name="input_items"
        label={t('operator.work_orders.detail.inputs_title')}
        description={t('operator.work_orders.detail.inputs_description')}
        merchantId={merchantId}
        inventoryTypes={inventoryTypes}
        inventoryAttributeDefinitions={inventoryAttributeDefinitions}
        role="input"
      />

      <FloatingActionBarSpacer />
    </Form>
    </Spin>

    <FloatingActionBar
      role="operator"
      onCancel={goBack}
      onSubmit={() => form.submit()}
      loading={submitting}
      disabled={savingAndAdding}
      extraButtons={isNew ? (
        <Button
          loading={savingAndAdding}
          disabled={submitting}
          onClick={handleSaveAndAddAnother}
        >
          {t('common.button.save_and_add_another')}
        </Button>
      ) : null}
    />
  </>
  );
};

export default WorkOrderForm;
